/**
 * H3.1 — RuntimeStatusBanner + useRuntimeStatus
 *
 * Single source of truth for per-client runtime copy in the admin UI.
 * Replaces the legacy hardcoded "Runtime status: NOT ACTIVE" banner
 * that no longer reflects reality for blueprint-active clients.
 *
 * Two flavours of copy:
 *   • BLUEPRINT aktiv  → emerald banner with what blueprint runtime
 *                        is currently doing (published blueprint +
 *                        presentation + actions + lead capture).
 *   • LEGACY           → neutral slate banner stating the client still
 *                        uses handymen-runtime; blueprint is granskning.
 *
 * Optionally accepts a `contextHint` string for tab-specific tail copy
 * (e.g. "Replay är side-effect free." in the replay tab).
 */
import { useCallback, useEffect, useState } from "react";
import { Activity, ShieldCheck, ShieldAlert, Loader2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { API_URL } from "../utils/api";

export function useRuntimeStatus(clientId) {
  const { getAuthHeaders } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(
        `${API_URL}/api/admin/runtime/status/${encodeURIComponent(clientId)}`,
        { headers: getAuthHeaders(), credentials: "include" },
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setStatus(await r.json());
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [clientId, getAuthHeaders]);

  useEffect(() => { refresh(); }, [refresh]);

  return { status, loading, error, refresh };
}

/**
 * RuntimeStatusBanner — displays product-truth copy per client.
 *
 * @param {object}  props
 * @param {string}  props.clientId
 * @param {string=} props.contextHint - extra context-specific sentence
 * @param {string=} props.testid - data-testid to use (default "runtime-status-banner")
 */
export default function RuntimeStatusBanner({
  clientId, contextHint, testid = "runtime-status-banner",
}) {
  const { status, loading, error } = useRuntimeStatus(clientId);

  if (loading && !status) {
    return (
      <div
        className="rounded-lg border border-slate-200 bg-slate-50 p-3 flex items-center gap-2"
        data-testid={`${testid}-loading`}
      >
        <Loader2 className="w-4 h-4 text-slate-500 animate-spin" />
        <span className="text-xs text-slate-600">Hämtar runtime-status…</span>
      </div>
    );
  }
  if (error || !status) {
    return (
      <div
        className="rounded-lg border border-rose-300 bg-rose-50 p-3 flex items-start gap-2"
        data-testid={`${testid}-error`}
      >
        <ShieldAlert className="w-4 h-4 text-rose-700 mt-0.5 shrink-0" />
        <p className="text-xs text-rose-900">
          Kunde inte läsa runtime-status: {error || "okänt fel"}.
        </p>
      </div>
    );
  }

  const active = status.active_runtime;          // "blueprint" | "legacy"
  const isBlueprint = active === "blueprint";
  const hasBlueprint = !!status.has_published_blueprint;
  const leadCaptureOn = !!status.lead_capture_enabled;
  const approvedActions = status.approved_actions_count || 0;
  const version = status.blueprint_version;

  if (isBlueprint) {
    return (
      <div
        className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 flex items-start gap-2"
        data-testid={testid}
        data-runtime="blueprint"
      >
        <ShieldCheck className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
        <div className="text-xs text-emerald-900 leading-snug space-y-1">
          <p>
            <strong>Runtime: BLUEPRINT aktiv.</strong>{" "}
            Denna klient använder publicerad Agent Blueprint
            {version ? <> v{version}</> : null},
            Widget Presentation och Approved Actions ({approvedActions} st).
            {leadCaptureOn
              ? " Blueprint-native lead capture är på."
              : " Lead capture är pausad via rollback-flagga."}
          </p>
          <p className="text-emerald-800/80">
            Rollback finns via <code>runtime_source = handymen</code>.
            {contextHint ? ` ${contextHint}` : ""}
          </p>
        </div>
      </div>
    );
  }

  // LEGACY
  const blueprintHint = hasBlueprint
    ? "Blueprint kan granskas och förberedas utan att påverka chatten."
    : "Ingen aktiv blueprint — klienten kör legacy-runtime fullt ut.";
  return (
    <div
      className="rounded-lg border border-slate-300 bg-slate-50 p-3 flex items-start gap-2"
      data-testid={testid}
      data-runtime="legacy"
    >
      <Activity className="w-4 h-4 text-slate-600 mt-0.5 shrink-0" />
      <div className="text-xs text-slate-800 leading-snug space-y-1">
        <p>
          <strong>Runtime: LEGACY.</strong>{" "}
          Denna klient använder fortfarande <code>handymen</code>-runtime
          för chatten.
        </p>
        <p className="text-slate-700/90">
          {blueprintHint}
          {contextHint ? ` ${contextHint}` : ""}
        </p>
      </div>
    </div>
  );
}
